import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info, X } from 'lucide-react';

import { cn } from '../../../lib/utils';

// How this page works (Nic 2026-10-29): the info icon beside a desk's tabs
// opens a panel from the LEFT edge. It slides in and out for real (the
// panel stays mounted through its exit transition), a faint scrim says
// "click anywhere to close", and Escape, the X or the icon close it too.
// The page underneath stays visible. One shell for Sales' Signing Up and
// Onboarding's Clients, each handing in its own content:
//   sections: the things to know, each a card with its own tone, icon,
//             title, a sentence, and optional fact chips
//   colors:   the color key, as solid swatches (Sales)
//   steps:    a vertical stepper (the windows on Sales, the spots on Clients)
//   notes:    the small print, as quiet cards at the end
const SLIDE_MS = 320;

const TONES = {
  sky: {
    tile: 'bg-sky-100 text-sky-700 dark:!bg-sky-500/20 dark:text-sky-200',
    rule: 'border-sky-200/80 dark:border-sky-400/30',
    chip: 'bg-sky-50 text-sky-700 ring-sky-200/70 dark:!bg-sky-500/10 dark:text-sky-200 dark:ring-sky-400/30',
  },
  amber: {
    tile: 'bg-amber-100 text-amber-700 dark:!bg-amber-500/20 dark:text-amber-200',
    rule: 'border-amber-200/80 dark:border-amber-400/30',
    chip: 'bg-amber-50 text-amber-800 ring-amber-200/70 dark:!bg-amber-500/10 dark:text-amber-200 dark:ring-amber-400/30',
  },
  rose: {
    tile: 'bg-rose-100 text-rose-700 dark:!bg-rose-500/20 dark:text-rose-200',
    rule: 'border-rose-200/80 dark:border-rose-400/30',
    chip: 'bg-rose-50 text-rose-700 ring-rose-200/70 dark:!bg-rose-500/10 dark:text-rose-200 dark:ring-rose-400/30',
  },
  violet: {
    tile: 'bg-violet-100 text-violet-700 dark:!bg-violet-500/20 dark:text-violet-200',
    rule: 'border-violet-200/80 dark:border-violet-400/30',
    chip: 'bg-violet-50 text-violet-700 ring-violet-200/70 dark:!bg-violet-500/10 dark:text-violet-200 dark:ring-violet-400/30',
  },
  emerald: {
    tile: 'bg-emerald-100 text-emerald-700 dark:!bg-emerald-500/20 dark:text-emerald-200',
    rule: 'border-emerald-200/80 dark:border-emerald-400/30',
    chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200/70 dark:!bg-emerald-500/10 dark:text-emerald-200 dark:ring-emerald-400/30',
  },
  slate: {
    tile: 'bg-slate-200 text-slate-700 dark:!bg-white/10 dark:text-slate-200',
    rule: 'border-slate-200 dark:border-white/10',
    chip: 'bg-slate-100 text-slate-700 ring-slate-200/70 dark:!bg-white/10 dark:text-slate-200 dark:ring-white/10',
  },
};

const Eyebrow = ({ children }) => (
  <div className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500">
    {children}
  </div>
);

const PageGuide = ({
  eyebrow = 'How this page works',
  title,
  intro,
  sections = [],
  colors = null,
  steps = null,
  notes = [],
}) => {
  const label = `How ${title} works`;
  // closed -> open (mounted, then slid in a frame later) -> closing
  // (slid out, unmounted when the transition is over).
  const [phase, setPhase] = useState('closed');
  const [shown, setShown] = useState(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);
  const timer = useRef(null);

  const open = () => {
    clearTimeout(timer.current);
    setPhase('open');
  };
  const close = () => {
    if (phase !== 'open') return;
    setPhase('closing');
    setShown(false);
    timer.current = setTimeout(() => setPhase('closed'), SLIDE_MS);
  };

  // Slide in a frame after mounting, so the transition has a start state.
  useEffect(() => {
    if (phase !== 'open') return undefined;
    const raf = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  useEffect(() => () => clearTimeout(timer.current), []);

  // Escape, and any click outside the panel (the icon toggles on its own).
  useEffect(() => {
    if (phase !== 'open') return undefined;
    const onKey = (e) => e.key === 'Escape' && close();
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (buttonRef.current?.contains(e.target)) return;
      close();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const isOpen = phase === 'open';

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => (isOpen ? close() : open())}
        aria-label={label}
        aria-expanded={isOpen}
        title={isOpen ? undefined : label}
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60',
          isOpen
            ? 'bg-slate-200/80 text-slate-700 dark:!bg-white/15 dark:text-slate-100'
            : 'text-slate-400 hover:bg-slate-200/70 hover:text-slate-600 dark:hover:!bg-white/10 dark:hover:text-slate-200',
        )}
      >
        <Info size={16} />
      </button>
      {phase !== 'closed'
        ? createPortal(
            <div className="fixed inset-0 z-[1055]">
              {/* A faint scrim: the page stays readable, and it says the
                  panel closes on a click anywhere. */}
              <div
                aria-hidden
                className={cn(
                  'absolute inset-0 bg-slate-900/[0.08] transition-opacity duration-300 ease-out motion-reduce:transition-none dark:bg-black/25',
                  shown ? 'opacity-100' : 'opacity-0',
                )}
              />
              <aside
                ref={panelRef}
                aria-label={label}
                data-state={shown ? 'open' : 'closed'}
                className={cn(
                  'absolute inset-y-0 left-0 flex w-[400px] max-w-[calc(100vw-2rem)] flex-col bg-white shadow-[16px_0_48px_rgba(15,23,42,0.16)] transition-transform duration-[320ms] ease-[cubic-bezier(0.32,0.72,0,1)] will-change-transform motion-reduce:transition-none dark:!bg-surface-elevated',
                  shown ? 'translate-x-0' : '-translate-x-full',
                )}
              >
                {/* Header: the eyebrow and the tab's name, nothing else
                    (Nic 2026-10-29: shorter, no glow). */}
                <div className="relative shrink-0 border-b border-slate-200/70 px-5 pb-2.5 pt-2.5 dark:border-white/10">
                  <button
                    type="button"
                    onClick={close}
                    aria-label="Close"
                    className="absolute right-3 top-2.5 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 outline-none hover:bg-slate-900/[0.06] hover:text-slate-700 focus:outline-none dark:hover:!bg-white/10 dark:hover:text-slate-100"
                  >
                    <X size={17} />
                  </button>
                  <div className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-sky-600 dark:text-sky-300">
                    {eyebrow}
                  </div>
                  <div className="text-[21px] font-extrabold leading-tight tracking-tight text-slate-900 dark:text-white">
                    {title}
                  </div>
                  {intro ? (
                    <div className="mt-1.5 max-w-[30ch] text-[13px] leading-snug text-slate-600 dark:text-slate-300">
                      {intro}
                    </div>
                  ) : null}
                </div>

                {/* The board's scrollbar (onboardingSupport.css): no track,
                    no arrows, the dark accent thumb. */}
                <div className="os-scroll os-thumb-dark mr-1.5 min-h-0 flex-1 overflow-y-auto py-5 pl-5 pr-3.5">
                  {/* The things to know, one card each. */}
                  <div className="space-y-2.5">
                    {sections.map((s) => {
                      const tone = TONES[s.tone] || TONES.slate;
                      return (
                        <section
                          key={s.title}
                          className={cn(
                            'rounded-2xl border bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_4px_14px_rgba(15,23,42,0.05)] dark:!bg-white/[0.035]',
                            tone.rule,
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <span
                              className={cn(
                                'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
                                tone.tile,
                              )}
                            >
                              <s.icon size={17} strokeWidth={2.2} />
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="text-[14px] font-bold leading-tight text-slate-900 dark:text-white">
                                {s.title}
                              </div>
                              <div className="mt-1 text-[12.5px] leading-[1.45] text-slate-600 dark:text-slate-300">
                                {s.text}
                              </div>
                              {s.facts?.length ? (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {s.facts.map((f) => (
                                    <span
                                      key={f}
                                      className={cn(
                                        'rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1',
                                        tone.chip,
                                      )}
                                    >
                                      {f}
                                    </span>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </section>
                      );
                    })}
                  </div>

                  {/* The color key, as the real colors. */}
                  {colors ? (
                    <div className="mt-6">
                      <Eyebrow>The colors beside Next</Eyebrow>
                      <div className="grid grid-cols-3 gap-2">
                        {colors.map((c) => (
                          <div
                            key={c.label}
                            className="rounded-xl px-2.5 py-2 text-white shadow-[0_1px_2px_rgba(15,23,42,0.2)]"
                            style={{ background: c.fill }}
                          >
                            <div className="text-[12px] font-bold leading-tight">
                              {c.label}
                            </div>
                            <div className="mt-0.5 text-[11px] leading-snug text-white/85">
                              {c.means}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/* A vertical stepper: the path, with a value per step. */}
                  {steps ? (
                    <div className="mt-6">
                      <Eyebrow>{steps.title}</Eyebrow>
                      {steps.text ? (
                        <div className="mb-2.5 text-[12px] leading-snug text-slate-500 dark:text-slate-400">
                          {steps.text}
                        </div>
                      ) : null}
                      <ol className="relative mb-0 list-none space-y-0 pl-0">
                        <span
                          aria-hidden
                          className="absolute bottom-3 left-[7px] top-3 w-px bg-slate-200 dark:bg-white/10"
                        />
                        {steps.items.map((it) => (
                          <li
                            key={it.name}
                            className="relative flex items-center gap-3 py-1.5 pl-6"
                          >
                            <span
                              aria-hidden
                              className={cn(
                                'absolute left-[4px] h-[7px] w-[7px] rounded-full ring-2 ring-white dark:ring-surface-elevated',
                                it.dot || 'bg-slate-400 dark:bg-slate-500',
                              )}
                            />
                            <span className="min-w-0 flex-1 text-[12.5px] font-semibold text-slate-800 dark:text-slate-100">
                              {it.name}
                            </span>
                            <span className="shrink-0 rounded-full bg-slate-100 px-2 text-[11px] font-semibold leading-5 tabular-nums text-slate-600 dark:!bg-white/10 dark:text-slate-300">
                              {it.value}
                            </span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  ) : null}

                  {/* The small print. */}
                  {notes.length ? (
                    <div className="mt-6">
                      <Eyebrow>Good to know</Eyebrow>
                      <div className="space-y-2">
                        {notes.map((n) => (
                          <div
                            key={n.title}
                            className="rounded-xl bg-slate-50 px-3.5 py-2.5 dark:!bg-white/[0.04]"
                          >
                            <div className="text-[12.5px] font-bold text-slate-800 dark:text-slate-100">
                              {n.title}
                            </div>
                            <div className="mt-0.5 text-[12px] leading-snug text-slate-600 dark:text-slate-400">
                              {n.text}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </aside>
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

export default PageGuide;
